import {isNil} from 'ramda';

import type {BusinessCaseActivityOnlineOfferEngagementResponseBody} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {buildArray, type Nullish} from 'shared/web';

import {ENGAGEMENT_SEGMENT_SEPARATOR} from '../constants/ENGAGEMENT_SEGMENT_SEPARATOR';
import {formatEngagementActiveTime} from './formatEngagementActiveTime';
import {getEngagementScrollSegment} from './getEngagementScrollSegment';

const FIRST_OPENING_KEY = 'page.businessCase.labels.onlineOfferEngagement.firstOpening';

/**
 * Builds the engagement line shown on the activity that records the customer opening
 * the online offer. The statistic always describes the first opening; later openings
 * never change it.
 *
 * Returns null when there is nothing to report, so callers can omit the line entirely.
 * That covers activities created before engagement tracking shipped (no engagement at
 * all) and openings where no measurement ever arrived (zero time, no scrolling).
 */
export const getActivityOnlineOfferEngagementSummary = (
  engagement: BusinessCaseActivityOnlineOfferEngagementResponseBody | Nullish
) => {
  if (isNil(engagement)) {
    return null;
  }

  const activeTimeSegment = formatEngagementActiveTime(engagement.activeTimeMs);
  const scrollSegment = getEngagementScrollSegment(engagement.maxScrollDepth);

  if (isNil(activeTimeSegment) && isNil(scrollSegment)) {
    return null;
  }

  return buildArray<string>()
    .add(i18n.t(FIRST_OPENING_KEY))
    .whenNotNil(activeTimeSegment)
    .whenNotNil(scrollSegment)
    .join(ENGAGEMENT_SEGMENT_SEPARATOR);
};
